import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import useBreakpoint from '../hooks/useBreakpoint';

const T = {
  ink:    '#111',
  muted:  '#888',
  border: '#e5e5e5',
  bg:     '#f9fafb',
};

/* ── Status config ── */
const S = {
  paid:    { label: 'Bezahlt',    color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
  pending: { label: 'Offen',      color: '#b45309', bg: '#fffbeb', border: '#fde68a' },
  partial: { label: 'Teilweise',  color: '#111', bg: '#f5f5f5', border: T.border },
  overdue: { label: 'Überfällig', color: '#dc2626', bg: '#fff1f2', border: '#fecaca' },
};

const REMINDER_LABELS = { 1: 'Zahlungserinnerung', 2: '1. Mahnung', 3: '2. Mahnung' };
const STATUS_SORT      = { overdue: 0, pending: 1, partial: 2, paid: 3 };

/* ── Formatters ── */
const fmtEur = v =>
  (!v && v !== 0) ? '–'
  : Number(v).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
const fmtEurRound = v =>
  (!v && v !== 0) ? '–'
  : Number(v).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' €';
const fmt = d =>
  d ? new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '–';
const monthLabel = (y, m) =>
  new Date(y, m - 1, 1).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });

function calcDueDate(year, month, rentDueDay) {
  if (!rentDueDay || rentDueDay === 'end_of_month')
    return new Date(year, month, 0).toISOString().split('T')[0];
  const last = new Date(year, month, 0).getDate();
  const day  = Math.min(parseInt(rentDueDay) || 1, last);
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}
function monthRange(year, month) {
  return {
    start: `${year}-${String(month).padStart(2, '0')}-01`,
    end:   new Date(year, month, 0).toISOString().split('T')[0],
  };
}
function isActiveInMonth(t, year, month) {
  if (!t.lease_start || !t.monthly_rent) return false;
  const s  = new Date(t.lease_start);
  const ms = new Date(year, month - 1, 1);
  const me = new Date(year, month, 0);
  if (s > me) return false;
  if (t.lease_end && new Date(t.lease_end) < ms) return false;
  return true;
}

/* ── Status badge ── */
function Badge({ status }) {
  const cfg = S[status] || S.pending;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      fontSize: 12, fontWeight: 700, padding: '4px 11px', borderRadius: 99,
      background: cfg.bg, border: `1.5px solid ${cfg.border}`, color: cfg.color,
      whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.color, flexShrink: 0 }} />
      {cfg.label}
    </span>
  );
}

/* ══════════════════════════════════════
   Desktop row
══════════════════════════════════════ */
function PaymentRow({ tenant, payment, month, year, onMarkPaid, onOpenRemind, onEdit, nextReminderLevel, isMarking, last }) {
  const status  = payment?.status || 'pending';
  const amount  = payment ? Number(payment.amount_due) : Number(tenant.monthly_rent) || 0;
  const isPaid  = status === 'paid';
  const canAct  = !isPaid;
  const canRem  = canAct && payment && nextReminderLevel !== null;

  return (
    <div
      style={{
        display: 'grid', gridTemplateColumns: '1fr 160px 140px 140px 240px',
        alignItems: 'center', padding: '14px 24px',
        borderBottom: last ? 'none' : `1px solid ${T.border}`,
        background: status === 'overdue' ? 'rgba(254,242,242,0.55)' : 'white',
        transition: 'background 0.1s',
      }}
      onMouseEnter={e => e.currentTarget.style.background = status === 'overdue' ? 'rgba(254,242,242,0.85)' : T.bg}
      onMouseLeave={e => e.currentTarget.style.background = status === 'overdue' ? 'rgba(254,242,242,0.55)' : 'white'}
    >
      {/* Mieter */}
      <div>
        <div style={{ fontWeight: 700, fontSize: 14, color: isPaid ? T.muted : T.ink }}>
          {tenant.first_name} {tenant.last_name}
        </div>
        {payment?.due_date && !isPaid && (
          <div style={{ fontSize: 11, color: status === 'overdue' ? '#dc2626' : T.muted, marginTop: 2, fontWeight: status === 'overdue' ? 600 : 400 }}>
            Fällig {fmt(payment.due_date)}
          </div>
        )}
      </div>

      {/* Monat */}
      <div style={{ fontSize: 13, color: isPaid ? T.muted : T.ink }}>
        {monthLabel(year, month)}
      </div>

      {/* Betrag */}
      <div style={{ fontSize: 15, fontWeight: 800, color: isPaid ? T.muted : T.ink, letterSpacing: '-0.4px' }}>
        {fmtEur(amount)}
      </div>

      {/* Status */}
      <div>
        <Badge status={status} />
      </div>

      {/* Aktionen */}
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
        {isPaid ? (
          <button onClick={() => onEdit({ tenant, payment })} className="btn-ghost"
            style={{ fontSize: 12, height: 34, padding: '0 14px' }}>
            Bearbeiten
          </button>
        ) : (
          <>
            {canRem && (
              <button
                onClick={() => onOpenRemind(payment, nextReminderLevel)}
                style={{
                  height: 36, padding: '0 12px', fontSize: 12, fontWeight: 600,
                  background: '#fffbeb', border: '1.5px solid #fde68a',
                  borderRadius: 8, color: '#b45309', cursor: 'pointer',
                  whiteSpace: 'nowrap', transition: 'background 0.1s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#fef3c7'}
                onMouseLeave={e => e.currentTarget.style.background = '#fffbeb'}
              >
                Mahnung senden
              </button>
            )}
            <button
              onClick={() => onMarkPaid(payment, tenant)}
              disabled={isMarking}
              className="btn-primary"
              style={{ height: 36, padding: '0 14px', fontSize: 12, opacity: isMarking ? 0.6 : 1, whiteSpace: 'nowrap' }}
            >
              {isMarking ? '…' : '✓ Zahlung erhalten'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   Mobile card
══════════════════════════════════════ */
function PaymentCard({ tenant, payment, month, year, onMarkPaid, onOpenRemind, onEdit, nextReminderLevel, isMarking }) {
  const status  = payment?.status || 'pending';
  const amount  = payment ? Number(payment.amount_due) : Number(tenant.monthly_rent) || 0;
  const isPaid  = status === 'paid';
  const canRem  = !isPaid && payment && nextReminderLevel !== null;
  const isOvd   = status === 'overdue';

  return (
    <div style={{
      background: isOvd ? '#fff8f8' : 'white',
      border: `1.5px solid ${isOvd ? '#fecaca' : T.border}`,
      borderRadius: 14, padding: '18px',
      boxShadow: isOvd ? '0 2px 8px rgba(220,38,38,0.07)' : '0 1px 4px rgba(0,0,0,0.05)',
    }}>
      {/* Top */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: isPaid ? T.muted : T.ink }}>
            {tenant.first_name} {tenant.last_name}
          </div>
          <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>{monthLabel(year, month)}</div>
        </div>
        <Badge status={status} />
      </div>

      {/* Amount + due */}
      <div style={{ display: 'flex', gap: 24, marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: T.muted, marginBottom: 3 }}>Betrag</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: isPaid ? T.muted : T.ink, letterSpacing: '-0.5px' }}>{fmtEur(amount)}</div>
        </div>
        {payment?.due_date && !isPaid && (
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: T.muted, marginBottom: 3 }}>Fällig</div>
            <div style={{ fontSize: 14, fontWeight: isOvd ? 700 : 500, color: isOvd ? '#dc2626' : T.ink }}>{fmt(payment.due_date)}</div>
          </div>
        )}
      </div>

      {/* Actions */}
      {isPaid ? (
        <button onClick={() => onEdit({ tenant, payment })} className="btn-ghost" style={{ width: '100%', height: 40, fontSize: 13 }}>
          Bearbeiten
        </button>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button onClick={() => onMarkPaid(payment, tenant)} disabled={isMarking} className="btn-primary"
            style={{ height: 46, fontSize: 15, fontWeight: 700, opacity: isMarking ? 0.6 : 1 }}>
            {isMarking ? '…' : '✓ Zahlung erhalten'}
          </button>
          {canRem && (
            <button onClick={() => onOpenRemind(payment, nextReminderLevel)}
              style={{ height: 42, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                background: '#fffbeb', border: '1.5px solid #fde68a', borderRadius: 8, color: '#b45309' }}>
              Mahnung senden
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════
   Main
══════════════════════════════════════ */
const inputStyle = {
  width: '100%', border: `1px solid ${T.border}`, padding: '10px 12px',
  fontSize: 13, boxSizing: 'border-box', color: T.ink, background: 'white',
  outline: 'none', borderRadius: 8,
};
function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.muted, marginBottom: 6 }}>{label}</label>
      {children}
    </div>
  );
}

export default function Payments() {
  const { user }     = useAuth();
  const { addToast } = useToast();
  const { isMobile } = useBreakpoint();
  const navigate     = useNavigate();

  const now = new Date();
  const [year, setYear]   = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [filter, setFilter] = useState('all');

  const [tenants, setTenants]       = useState([]);
  const [payments, setPayments]     = useState([]);
  const [reminders, setReminders]   = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [syncing, setSyncing]       = useState(false);
  const [marking, setMarking]       = useState(null);

  const [modal, setModal]         = useState(null);
  const [modalForm, setModalForm] = useState({ amount_paid: '', paid_date: '', notes: '' });
  const [saving, setSaving]       = useState(false);
  const [formError, setFormError] = useState('');


  /* ── Data fetching ── */
  const fetchAll = useCallback(async () => {
    const [{ data: t }, { data: p }] = await Promise.all([
      supabase.from('tenants')
        .select('id,first_name,last_name,unit,monthly_rent,lease_start,lease_end,property_id,rent_due_day')
        .eq('owner_id', user.id).order('last_name'),
      supabase.from('properties').select('id,name').eq('owner_id', user.id),
    ]);
    setTenants(t || []);
    setProperties(p || []);
    return t || [];
  }, [user.id]);

  const fetchPayments = useCallback(async () => {
    const { start, end } = monthRange(year, month);
    const { data } = await supabase.from('rent_payments').select('*')
      .eq('owner_id', user.id).gte('due_date', start).lte('due_date', end);
    return data || [];
  }, [year, month, user.id]);

  const fetchReminders = useCallback(async (ids) => {
    if (!ids.length) return [];
    const { data } = await supabase.from('reminders')
      .select('id,rent_payment_id,level,status').in('rent_payment_id', ids).neq('status', 'cancelled');
    return data || [];
  }, []);

  const sync = useCallback(async (tenantList) => {
    setSyncing(true);
    const today = new Date().toISOString().split('T')[0];
    const { start, end } = monthRange(year, month);
    const active = tenantList.filter(t => isActiveInMonth(t, year, month));

    const { data: existing } = await supabase.from('rent_payments')
      .select('tenant_id').eq('owner_id', user.id).gte('due_date', start).lte('due_date', end);
    const existingIds = new Set((existing || []).map(p => p.tenant_id));

    const toInsert = active.filter(t => !existingIds.has(t.id));
    if (toInsert.length) {
      await supabase.from('rent_payments').insert(toInsert.map(t => ({
        tenant_id: t.id, owner_id: user.id,
        due_date:  calcDueDate(year, month, t.rent_due_day),
        amount_due: Number(t.monthly_rent), amount_paid: 0, status: 'pending',
      })));
    }
    await supabase.from('rent_payments').update({ status: 'overdue' })
      .eq('owner_id', user.id).eq('status', 'pending').lte('due_date', today);

    const fresh = await fetchPayments();
    const rems  = await fetchReminders(fresh.map(p => p.id));
    setPayments(fresh);
    setReminders(rems);
    setSyncing(false);
  }, [year, month, user.id, fetchPayments, fetchReminders]);

  useEffect(() => {
    setLoading(true);
    fetchAll().then(t => sync(t).then(() => setLoading(false)));
  }, [year, month]); // eslint-disable-line

  /* ── Helpers ── */
  function getNextLevel(paymentId) {
    const sent = new Set(reminders.filter(r => r.rent_payment_id === paymentId && r.status === 'sent').map(r => r.level));
    for (const l of [1, 2, 3]) { if (!sent.has(l)) return l; }
    return null;
  }

  function prevMonth() {
    if (month === 1) { setYear(y => y - 1); setMonth(12); } else setMonth(m => m - 1);
  }
  function nextMonth() {
    const ny = month === 12 ? year + 1 : year;
    const nm = month === 12 ? 1 : month + 1;
    if (ny > now.getFullYear() || (ny === now.getFullYear() && nm > now.getMonth() + 1)) return;
    if (month === 12) { setYear(y => y + 1); setMonth(1); } else setMonth(m => m + 1);
  }
  const isCurrentMonth = year === now.getFullYear() && month === now.getMonth() + 1;

  function propName(id) {
    return properties.find(p => p.id === id)?.name || null;
  }

  /* ── Handlers ── */
  async function handleMarkPaid(payment, tenant) {
    const amount = Number(payment?.amount_due ?? tenant.monthly_rent);
    const today  = new Date().toISOString().split('T')[0];
    const id     = payment?.id ?? `new-${tenant.id}`;
    setMarking(id);
    try {
      if (payment) {
        const { error } = await supabase.from('rent_payments')
          .update({ amount_paid: amount, paid_date: today, status: 'paid' })
          .eq('id', payment.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('rent_payments').insert({
          tenant_id: tenant.id, owner_id: user.id,
          due_date:  calcDueDate(year, month, tenant.rent_due_day),
          amount_due: amount, amount_paid: amount, paid_date: today, status: 'paid',
        });
        if (error) throw error;
      }
      const fresh = await fetchPayments();
      const rems  = await fetchReminders(fresh.map(p => p.id));
      setPayments(fresh); setReminders(rems);
      addToast('Zahlung als bezahlt markiert.', 'success');
    } catch (err) {
      addToast('Fehler: ' + err.message, 'error');
    } finally {
      setMarking(null);
    }
  }

  function navigateToReminder(payment) {
    const tenant = tenants.find(t => t.id === payment.tenant_id);
    const name = tenant ? `${tenant.first_name} ${tenant.last_name}` : 'Mieter';
    const betrag = payment.amount_due ? `${Number(payment.amount_due).toLocaleString('de-DE')} €` : '–';
    const monat = new Date(payment.due_date).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
    navigate('/nachrichten', {
      state: {
        prefill: {
          type: 'reminder',
          tenantId: tenant?.id,
          tenantName: name,
          tenantEmail: tenant?.email || '',
          betreff: 'Offene Miete',
          inhalt: `Hallo ${name},\n\nfür die Miete in Höhe von ${betrag} (${monat}) besteht aktuell ein offener Betrag.\nBitte überweise diesen zeitnah.\n\nMit freundlichen Grüßen`,
        },
      },
    });
  }

  async function handleSaveEdit(e) {
    e.preventDefault(); setSaving(true); setFormError('');
    const amountPaid = Number(modalForm.amount_paid);
    const amountDue  = Number(modal.payment?.amount_due ?? modal.tenant.monthly_rent);
    const status     = amountPaid >= amountDue ? 'paid' : amountPaid > 0 ? 'partial' : 'pending';
    const op = modal.payment
      ? supabase.from('rent_payments').update({ amount_paid: amountPaid, paid_date: modalForm.paid_date || null, notes: modalForm.notes || null, status }).eq('id', modal.payment.id)
      : supabase.from('rent_payments').insert({ tenant_id: modal.tenant.id, owner_id: user.id, due_date: calcDueDate(year, month, modal.tenant.rent_due_day), amount_due: amountDue, amount_paid: amountPaid, paid_date: modalForm.paid_date || null, notes: modalForm.notes || null, status });
    const { error } = await op;
    if (error) { setFormError(error.message); setSaving(false); return; }
    const fresh = await fetchPayments();
    const rems  = await fetchReminders(fresh.map(p => p.id));
    setPayments(fresh); setReminders(rems);
    setModal(null); setFormError(''); setSaving(false);
    addToast('Zahlung gespeichert.', 'success');
  }

  /* ── Derived ── */
  const active = tenants.filter(t => isActiveInMonth(t, year, month));
  const rows = active
    .map(t => ({ tenant: t, payment: payments.find(p => p.tenant_id === t.id) || null }))
    .sort((a, b) => (STATUS_SORT[a.payment?.status || 'pending'] ?? 1) - (STATUS_SORT[b.payment?.status || 'pending'] ?? 1));

  const filtered = rows.filter(({ payment }) => {
    const s = payment?.status || 'pending';
    if (filter === 'overdue') return s === 'overdue';
    if (filter === 'open')    return s === 'pending';
    if (filter === 'paid')    return s === 'paid';
    return true;
  });

  const totalDue     = rows.reduce((s, r) => s + (r.payment ? Number(r.payment.amount_due) : Number(r.tenant.monthly_rent) || 0), 0);
  const totalPaid    = rows.reduce((s, r) => s + (r.payment ? Number(r.payment.amount_paid) : 0), 0);
  const countPaid    = rows.filter(r => r.payment?.status === 'paid').length;
  const countOpen    = rows.filter(r => !r.payment || r.payment.status === 'pending').length;
  const countOverdue = rows.filter(r => r.payment?.status === 'overdue').length;
  const busy         = loading || syncing;

  /* ══════════════════════════════════════
     Render
  ══════════════════════════════════════ */
  return (
    <div style={{ maxWidth: 1060, color: T.ink }}>

      {/* ── Page header ── */}
      <div style={{
        display: 'flex', flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center',
        marginBottom: 28, gap: 16,
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: isMobile ? 22 : 28, fontWeight: 800, letterSpacing: '-0.6px' }}>
            Zahlungsübersicht
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: 14, color: T.muted }}>Behalte alle Mietzahlungen im Blick.</p>
        </div>

        {/* Month navigator */}
        <div style={{
          display: 'inline-flex', alignItems: 'center',
          background: 'white', border: `1.5px solid ${T.border}`, borderRadius: 12, overflow: 'hidden',
        }}>
          <button onClick={prevMonth} style={{ background: 'none', border: 'none', width: 40, height: 42, cursor: 'pointer', fontSize: 20, color: T.muted, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>‹</button>
          <div style={{ padding: '0 18px', fontSize: 13, fontWeight: 700, color: T.ink, borderLeft: `1px solid ${T.border}`, borderRight: `1px solid ${T.border}`, height: 42, display: 'flex', alignItems: 'center', minWidth: 160, justifyContent: 'center' }}>
            {monthLabel(year, month)}
          </div>
          <button onClick={nextMonth} disabled={isCurrentMonth} style={{ background: 'none', border: 'none', width: 40, height: 42, cursor: isCurrentMonth ? 'default' : 'pointer', fontSize: 20, color: isCurrentMonth ? T.border : T.muted, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>›</button>
        </div>
      </div>

      {/* ── Summary cards ── */}
      {!busy && rows.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3,1fr)', gap: 12, marginBottom: 28 }}>

          {/* Offene Zahlungen */}
          <div style={{
            background: countOpen > 0 ? '#fffbeb' : 'white',
            border: `1.5px solid ${countOpen > 0 ? '#fde68a' : T.border}`,
            borderRadius: 16, padding: '20px 22px',
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: countOpen > 0 ? '#b45309' : T.muted, marginBottom: 10 }}>
              Offene Zahlungen
            </div>
            <div style={{ fontSize: 36, fontWeight: 900, color: countOpen > 0 ? '#b45309' : '#9ca3af', letterSpacing: '-1.5px', lineHeight: 1 }}>
              {countOpen}
            </div>
            <div style={{ fontSize: 12, color: T.muted, marginTop: 8 }}>
              {countOpen === 0 ? 'Alles bezahlt' : `${countOpen} von ${rows.length} Mietern`}
            </div>
          </div>

          {/* Überfällig */}
          <div style={{
            background: countOverdue > 0 ? '#fff1f2' : 'white',
            border: `1.5px solid ${countOverdue > 0 ? '#fecaca' : T.border}`,
            borderRadius: 16, padding: '20px 22px',
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: countOverdue > 0 ? '#dc2626' : T.muted, marginBottom: 10 }}>
              Überfällig
            </div>
            <div style={{ fontSize: 36, fontWeight: 900, color: countOverdue > 0 ? '#dc2626' : '#9ca3af', letterSpacing: '-1.5px', lineHeight: 1 }}>
              {countOverdue}
            </div>
            <div style={{ fontSize: 12, color: T.muted, marginTop: 8 }}>
              {countOverdue === 0 ? 'Keine überfälligen Zahlungen' : 'Sofortiger Handlungsbedarf'}
            </div>
          </div>

          {/* Einnahmen */}
          <div style={{
            background: totalPaid > 0 ? '#f0fdf4' : 'white',
            border: `1.5px solid ${totalPaid > 0 ? '#bbf7d0' : T.border}`,
            borderRadius: 16, padding: '20px 22px',
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: totalPaid > 0 ? '#15803d' : T.muted, marginBottom: 10 }}>
              Einnahmen
            </div>
            <div style={{ fontSize: 28, fontWeight: 900, color: totalPaid > 0 ? '#15803d' : '#9ca3af', letterSpacing: '-1px', lineHeight: 1 }}>
              {fmtEurRound(totalPaid)}
            </div>
            <div style={{ fontSize: 12, color: T.muted, marginTop: 8 }}>
              von {fmtEurRound(totalDue)} erwartet · {countPaid} bezahlt
            </div>
          </div>
        </div>
      )}

      {/* ── Filter tabs ── */}
      {!busy && rows.length > 0 && (
        <div className="filter-tabs" style={{ marginBottom: 18 }}>
          {[
            { key: 'all',     label: 'Alle',        count: rows.length   },
            { key: 'overdue', label: 'Überfällig',  count: countOverdue  },
            { key: 'open',    label: 'Offen',       count: countOpen     },
            { key: 'paid',    label: 'Bezahlt',     count: countPaid     },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`filter-tab${filter === tab.key ? ' active' : ''}`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span style={{
                  marginLeft: 6, fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 100,
                  background: filter === tab.key ? T.ink : T.border,
                  color: filter === tab.key ? 'white' : T.muted,
                }}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* ── Content ── */}
      {busy ? (
        /* Skeleton */
        <div style={{ background: 'white', border: `1px solid ${T.border}`, borderRadius: 16, overflow: 'hidden' }}>
          {[1,2,3,4].map(n => (
            <div key={n} style={{ display: 'grid', gridTemplateColumns: '1fr 160px 140px 140px 200px', gap: 16, padding: '18px 24px', borderBottom: n < 4 ? `1px solid ${T.border}` : 'none', alignItems: 'center' }}>
              <div><div className="skeleton" style={{ width: '60%', height: 14, borderRadius: 6, marginBottom: 6 }} /><div className="skeleton" style={{ width: '40%', height: 11, borderRadius: 5 }} /></div>
              <div className="skeleton" style={{ height: 13, borderRadius: 5 }} />
              <div className="skeleton" style={{ height: 14, borderRadius: 5 }} />
              <div className="skeleton" style={{ width: 70, height: 24, borderRadius: 99 }} />
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}><div className="skeleton" style={{ width: 90, height: 34, borderRadius: 8 }} /><div className="skeleton" style={{ width: 110, height: 34, borderRadius: 8 }} /></div>
            </div>
          ))}
        </div>

      ) : rows.length === 0 ? (
        <div style={{ background:'white', border:`1.5px solid ${T.border}`, borderRadius:18, overflow:'hidden' }}>
          <div style={{ padding:'48px 40px' }}>
            <div style={{ display:'flex', alignItems:'flex-start', gap:32, marginBottom:40 }}>
              <div style={{ flexShrink:0, width:64, height:64, background:'#f7f7f7', borderRadius:16, display:'flex', alignItems:'center', justifyContent:'center', border:'1.5px solid e5e5e5' }}>
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:20, fontWeight:700, color:T.ink, marginBottom:8, letterSpacing:'-0.3px' }}>Keine Zahlungen vorhanden</div>
                <div style={{ fontSize:14, color:T.muted, lineHeight:1.8, maxWidth:520 }}>Für {monthLabel(year, month)} gibt es keine Zahlungseinträge. Lege zunächst Mieter mit einem aktiven Mietvertrag an.</div>
                <a href="/tenants" className="btn-primary" style={{ marginTop:20, display:'inline-flex', alignItems:'center', textDecoration:'none' }}>Zu den Mietern →</a>
              </div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3,1fr)', gap:16 }}>
              {[
                { icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>, title:'Automatisch generiert', text:'Sobald ein Mieter aktiv ist, werden Zahlungen automatisch pro Monat erstellt.' },
                { icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>, title:'Mahnwesen', text:'Überfällige Zahlungen werden direkt ins Mahnwesen weitergegeben.' },
                { icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>, title:'Monatsübersicht', text:'Navigiere durch Monate und behalte alle Zahlungen im Blick.' },
              ].map((f,i)=>(
                <div key={i} style={{ background:'#f7f7f7', borderRadius:12, padding:'18px 20px', border:`1px solid ${T.border}` }}>
                  <div style={{ width:36, height:36, background:'white', borderRadius:9, display:'flex', alignItems:'center', justifyContent:'center', marginBottom:12, border:`1px solid ${T.border}`, boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>{f.icon}</div>
                  <div style={{ fontSize:13, fontWeight:700, color:T.ink, marginBottom:4 }}>{f.title}</div>
                  <div style={{ fontSize:12, color:T.muted, lineHeight:1.6 }}>{f.text}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

      ) : filtered.length === 0 ? (
        <div style={{ background:'white', border:`1px solid ${T.border}`, borderRadius:16, textAlign:'center', padding:'48px 24px' }}>
          <div style={{ width:48, height:48, background:'#f7f7f7', borderRadius:12, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 14px' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          </div>
          <div style={{ fontSize:14, fontWeight:700, color:T.ink, marginBottom:6 }}>Keine Einträge in dieser Kategorie</div>
          <div style={{ fontSize:13, color:T.muted }}>Alle Zahlungen dieses Monats befinden sich in anderen Kategorien.</div>
        </div>

      ) : isMobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map(({ tenant: t, payment: p }) => (
            <PaymentCard
              key={t.id}
              tenant={t} payment={p}
              month={month} year={year}
              onMarkPaid={handleMarkPaid}
              onOpenRemind={(pay) => navigateToReminder(pay)}
              onEdit={row => { setModal(row); setModalForm({ amount_paid: String(row.payment?.amount_due ?? row.tenant.monthly_rent ?? ''), paid_date: new Date().toISOString().split('T')[0], notes: row.payment?.notes || '' }); setFormError(''); }}
              nextReminderLevel={p ? getNextLevel(p.id) : null}
              isMarking={marking === (p?.id ?? `new-${t.id}`)}
            />
          ))}
        </div>

      ) : (
        /* Desktop table */
        <div style={{ background: 'white', border: `1px solid ${T.border}`, borderRadius: 16, overflow: 'hidden', boxShadow: '0 1px 6px rgba(0,0,0,0.05)' }}>
          {/* Table header */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px 140px 140px 240px', padding: '11px 24px', background: T.bg, borderBottom: `1px solid ${T.border}` }}>
            {['Mieter', 'Monat', 'Betrag', 'Status', ''].map(h => (
              <span key={h} style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.6px', textTransform: 'uppercase', color: '#9ca3af' }}>{h}</span>
            ))}
          </div>
          {filtered.map(({ tenant: t, payment: p }, i) => (
            <PaymentRow
              key={t.id}
              tenant={t} payment={p}
              month={month} year={year}
              onMarkPaid={handleMarkPaid}
              onOpenRemind={(pay) => navigateToReminder(pay)}
              onEdit={row => { setModal(row); setModalForm({ amount_paid: String(row.payment?.amount_due ?? row.tenant.monthly_rent ?? ''), paid_date: new Date().toISOString().split('T')[0], notes: row.payment?.notes || '' }); setFormError(''); }}
              nextReminderLevel={p ? getNextLevel(p.id) : null}
              isMarking={marking === (p?.id ?? `new-${t.id}`)}
              last={i === filtered.length - 1}
            />
          ))}
        </div>
      )}

      {/* ══ Edit Modal ══ */}
      {modal && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: isMobile ? 0 : 24 }}
          onClick={e => { if (e.target === e.currentTarget) setModal(null); }}
        >
          <div className="immo-modal" style={{ background: 'white', width: '100%', maxWidth: 440 }}>
            <div style={{ padding: '24px 28px 18px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Zahlung erfassen</div>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>{modal.tenant.last_name}, {modal.tenant.first_name}</h2>
                <div style={{ fontSize: 12, color: T.muted, marginTop: 4 }}>
                  {monthLabel(year, month)} · Erwartet: {fmtEur(modal.payment?.amount_due ?? modal.tenant.monthly_rent)}
                </div>
              </div>
              <button onClick={() => setModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: T.muted, lineHeight: 1, padding: 4 }}>×</button>
            </div>
            <form onSubmit={handleSaveEdit} style={{ padding: '22px 28px 28px' }}>
              {formError && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#b91c1c', marginBottom: 16 }}>{formError}</div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14 }}>
                <Field label="Betrag (€)">
                  <input type="number" min="0" step="0.01" required value={modalForm.amount_paid} onChange={e => setModalForm({ ...modalForm, amount_paid: e.target.value })} style={inputStyle} />
                </Field>
                <Field label="Eingangsdatum">
                  <input type="date" value={modalForm.paid_date} onChange={e => setModalForm({ ...modalForm, paid_date: e.target.value })} style={inputStyle} />
                </Field>
              </div>
              <Field label="Notizen">
                <textarea rows={2} value={modalForm.notes} onChange={e => setModalForm({ ...modalForm, notes: e.target.value })} placeholder="Optionale Anmerkungen…" style={{ ...inputStyle, resize: 'vertical' }} />
              </Field>
              <div style={{ marginBottom: 18 }}>
                <button type="button" className="btn-ghost" style={{ fontSize: 12, height: 34 }}
                  onClick={() => setModalForm(f => ({ ...f, amount_paid: String(modal.payment?.amount_due ?? modal.tenant.monthly_rent) }))}>
                  Vollbetrag übernehmen ({fmtEur(modal.payment?.amount_due ?? modal.tenant.monthly_rent)})
                </button>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="submit" disabled={saving} className="btn-primary" style={{ flex: 1, height: 44, fontSize: 14, opacity: saving ? 0.6 : 1 }}>
                  {saving ? 'Speichert…' : 'Zahlung speichern'}
                </button>
                <button type="button" onClick={() => setModal(null)} className="btn-secondary" style={{ height: 44, fontSize: 14, padding: '0 20px' }}>
                  Abbrechen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
